import React, { useContext, useState } from 'react';
import '../localization/config';
import { useTranslation } from 'react-i18next';
import {
  TeamMemberData,
  TeamMembersContext,
} from '../providers/TeamMembersProvider';
// @ts-ignore
import { DataTable } from '@performant-software/semantic-components';
import ContentBox from '../components/ContentBox';
import { Button, Icon } from 'semantic-ui-react';
import AddTeamMemberModal from '../components/AddTeamMemberModal';
import { FlexRow } from '../layouts';
import ConfirmationModal from '../components/ConfirmationModal';
import { AuthenticationContext } from '../providers/AuthenticationProvider';

export interface ITeamMembersProps {}

const TeamMembers = (_props: ITeamMembersProps) => {
  const {
    teamMembers,
    availableUsers,
    teamId,
    onCreateTeamMember,
    onUpdateTeamMember,
    onDeleteTeamMember,
  } = useContext(TeamMembersContext);
  const { canManageTeamMembers } = useContext(AuthenticationContext);

  const { t } = useTranslation();

  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [activeTeamMember, setActiveTeamMember] = useState<
    TeamMemberData | undefined
  >();
  const [confirmTitle, setConfirmTitle] = useState<string>('');
  const [confirmMessage, setConfirmMessage] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [isAdd, setIsAdd] = useState<boolean>(false);

  const renderCheck = (key: string, item: any) => {
    return <div>{item[key] ? <Icon name="check" color="green" /> : ''}</div>;
  };

  const handleAddTeamMember = () => {
    setIsAdd(true);
    setActiveTeamMember({
      firstName: '',
      lastName: '',
      email: '',
      systemAdmin: false,
      teamAdmin: false,
      teamMemberAdmin: false,
      configAdmin: false,
      canCheckOut: false,
      canCreate: false,
      canDelete: false,
    });
    setAddOpen(true);
  };

  const handleSave = (item: TeamMemberData) => {
    if (!item.id) {
      onCreateTeamMember(item);
    } else {
      onUpdateTeamMember(item);
    }

    setAddOpen(false);
  };

  const handleEdit = (item: TeamMemberData) => {
    setActiveTeamMember(item);
    setAddOpen(true);
  };

  const handleDelete = (item: TeamMemberData) => {
    setActiveTeamMember(item);
    setConfirmTitle(t('teamMembers.confirmTeamMemberDeletion'));
    setConfirmMessage(
      `${t('teamMembers.doYouWishToDeleteTeamMember')} ${item.firstName} ${
        item.lastName
      }${t('general.?')}`
    );
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    onDeleteTeamMember(activeTeamMember?.id as number);
    setConfirmOpen(false);
    setActiveTeamMember(undefined);
  };

  const memberManager = teamId ? canManageTeamMembers(teamId) : false;
  const actions = memberManager
    ? [
        { name: 'edit', onClick: handleEdit },
        { name: 'delete', onClick: handleDelete },
      ]
    : [];

  const columns = [
    {
      name: 'lastName',
      label: t('teamMembers.lastName'),
      sortable: false,
    },
    {
      name: 'firstName',
      label: t('teamMembers.firstName'),
      sortable: false,
    },
    {
      name: 'email',
      label: t('teamMembers.email'),
      sortable: false,
    },
    {
      name: 'teamAdmin',
      label: t('teamMembers.teamAdmin'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('teamAdmin', item),
    },
    {
      name: 'teamMemberAdmin',
      label: t('teamMembers.teamMemberAdmin'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('teamMemberAdmin', item),
    },
    {
      name: 'configAdmin',
      label: t('teamMembers.configAdmin'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('configAdmin', item),
    },
    {
      name: 'canCheckOut',
      label: t('teamMembers.canCheckOut'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('canCheckOut', item),
    },
    {
      name: 'canCreate',
      label: t('teamMembers.canCreate'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('canCreate', item),
    },
    {
      name: 'canDelete',
      label: t('teamMembers.canDelete'),
      sortable: false,
      textAlign: 'center',
      render: (item: any) => renderCheck('canDelete', item),
    },
  ];

  return (
    <ContentBox widthPct={100} heightPct={75} padding={32}>
      {memberManager && (
        <FlexRow padBottom={10}>
          <Button onClick={handleAddTeamMember} color="green">
            <Icon name="add" />
            {t('general.add')}
          </Button>
        </FlexRow>
      )}
      <DataTable
        actions={actions}
        columns={columns}
        items={teamMembers}
        tableProps={{ width: '100%' }}
      />
      <AddTeamMemberModal
        open={addOpen}
        initialValues={activeTeamMember as TeamMemberData}
        isAdd={isAdd}
        availableUsers={availableUsers}
        onSubmit={handleSave}
        onCancel={() => setAddOpen(false)}
      />
      <ConfirmationModal
        title={confirmTitle}
        message={confirmMessage}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </ContentBox>
  );
};

export default TeamMembers;
